import { LOCAL_AUTH_SERVICE_ORIGIN } from '../../../config/auth-service';
import { getEnv } from '../../../utils/env';
import { getAppName } from '../../../utils/getAppName';
import { GetAuth0SessionResponse } from '../../types';

export const SSO_MESSAGE_TYPE = 'ross-auth:sso-check';

type SsoCheckMessage = {
  type: typeof SSO_MESSAGE_TYPE;
  status: 'authenticated' | 'unauthenticated' | 'error';
  isAuthenticated: boolean | null;
};

type CheckSsoOptions = {
  authServiceBaseUrl: string;
  retailUnit: string;
  lang: string;
  timeoutMs?: number;
};

let activeSsoCheck: Promise<boolean> | undefined;
const SSO_FAILURE_COOLDOWN_MS = 30_000;
let recentSsoFailure:
  | {
      expiresAt: number;
      result: false | Error;
    }
  | undefined;

export function resetSsoCheckCooldown() {
  recentSsoFailure = undefined;
}

export function checkSsoInIframe({
  authServiceBaseUrl,
  retailUnit,
  lang,
  timeoutMs = 10_000,
}: CheckSsoOptions): Promise<boolean> {
  if (activeSsoCheck) return activeSsoCheck;

  if (recentSsoFailure && recentSsoFailure.expiresAt > Date.now()) {
    return recentSsoFailure.result instanceof Error
      ? Promise.reject(recentSsoFailure.result)
      : Promise.resolve(recentSsoFailure.result);
  }

  recentSsoFailure = undefined;

  activeSsoCheck = new Promise<boolean>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    const authServiceOrigin = new URL(authServiceBaseUrl).origin;
    // The local auth redirect may finish on the proxy target's own origin.
    const callbackOrigin =
      getEnv() === 'development' && import.meta.env.VITE_LOCAL_AUTH === 'true' ? LOCAL_AUTH_SERVICE_ORIGIN : undefined;
    const authServiceRoot = `${authServiceBaseUrl.replace(/\/$/, '')}/`;
    const silentLoginUrl = new URL('silent-login/', authServiceRoot);

    silentLoginUrl.searchParams.set('retailUnit', retailUnit);
    silentLoginUrl.searchParams.set('lang', lang);
    silentLoginUrl.searchParams.set('iframeOrigin', globalThis.location.origin);

    iframe.hidden = true;
    iframe.title = 'Authentication status check';

    const cleanup = () => {
      globalThis.clearTimeout(timeout);
      globalThis.removeEventListener('message', onMessage);
      iframe.remove();
      activeSsoCheck = undefined;
    };

    const onMessage = (event: MessageEvent<unknown>) => {
      if (
        (event.origin !== authServiceOrigin && event.origin !== callbackOrigin) ||
        event.source !== iframe.contentWindow
      ) {
        return;
      }

      const message = event.data as Partial<SsoCheckMessage>;
      if (message.type !== SSO_MESSAGE_TYPE) return;
      if (!['authenticated', 'unauthenticated', 'error'].includes(message.status ?? '')) return;

      cleanup();

      if (message.status === 'error') {
        const error = new Error('Auth0 SSO status is temporarily unavailable');
        recentSsoFailure = {
          expiresAt: Date.now() + SSO_FAILURE_COOLDOWN_MS,
          result: error,
        };
        reject(error);
        return;
      }

      const isAuthenticated = message.status === 'authenticated' && message.isAuthenticated === true;
      if (!isAuthenticated) {
        recentSsoFailure = {
          expiresAt: Date.now() + SSO_FAILURE_COOLDOWN_MS,
          result: false,
        };
      }
      resolve(isAuthenticated);
    };

    const timeout = globalThis.setTimeout(() => {
      cleanup();
      const error = new Error('Auth0 SSO status check timed out');
      recentSsoFailure = {
        expiresAt: Date.now() + SSO_FAILURE_COOLDOWN_MS,
        result: error,
      };
      reject(error);
    }, timeoutMs);

    globalThis.addEventListener('message', onMessage);
    iframe.src = silentLoginUrl.toString();
    document.body.appendChild(iframe);
  });

  return activeSsoCheck;
}

export function shouldTryToSilentlyLogin(): boolean {
  if (globalThis.ikea?.mvAgent?.profilePagesFragmentLive) return false;
  if (getEnv() === 'development' && import.meta.env.VITE_LOCAL_AUTH !== 'true') return false;
  const appName = getAppName();
  return appName !== 'profile' && appName !== 'checkout';
}

export async function getVerifiedAuthStatus(
  authServiceBaseUrl: string,
  retailUnit: string,
  lang: string,
  signal?: AbortSignal,
  force = false,
): Promise<GetAuth0SessionResponse> {
  if (force) resetSsoCheckCooldown();
  if (shouldTryToSilentlyLogin()) {
    try {
      await checkSsoInIframe({ authServiceBaseUrl, retailUnit, lang });
    } catch {
      // Silent login is best effort; /status can still confirm an existing session.
    }
  }

  const controller = new AbortController();
  const abort = () => controller.abort(signal?.reason);
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const timeout = globalThis.setTimeout(() => {
    controller.abort(new DOMException('Authentication status timed out', 'TimeoutError'));
  }, 32_000);

  try {
    const response = await fetch(new URL('status/', `${authServiceBaseUrl.replace(/\/$/, '')}/`), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Authentication status failed: ${response.status}`);
    return (await response.json()) as GetAuth0SessionResponse;
  } finally {
    globalThis.clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
